import { useState } from 'react'
import { Button, Menu, MenuItem } from '@mui/material'
import { useElementLabel } from '@editorHooks'

interface Props {
  sources?: string[]
  onSelectSource: (guid: string) => void
}

const LoadSourceLabel = ({ sources = [], onSelectSource }: Props) => {
  const getLabel = useElementLabel()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  // Remove duplicates and generate labels, also deduplicate by label text
  const uniqueSources = Array.from(new Set(sources))
  const labelMap = new Map<string, string>()
  uniqueSources.forEach(s => {
    const label = s === 'extern' ? 'extern' : getLabel(s)
    if (!labelMap.has(label)) {
      labelMap.set(label, s)
    }
  })
  const sourceLabel = labelMap.size > 0 ? Array.from(labelMap.keys()).join(', ') : 'extern'

  // Get all clickable sources (excluding 'extern')
  const clickableSources = uniqueSources.filter(
    s => s && s !== 'extern' && typeof s === 'string' && s.trim().length > 0,
  )
  const hasClickableSource = clickableSources.length > 0

  // Filter out 'extern' from labelMap for dropdown
  const clickableLabelMap = new Map<string, string>()
  Array.from(labelMap.entries()).forEach(([label, guid]) => {
    if (guid !== 'extern') {
      clickableLabelMap.set(label, guid)
    }
  })
  const hasMultipleClickableSources = clickableLabelMap.size > 1

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (hasMultipleClickableSources) {
      setAnchorEl(event.currentTarget)
    } else if (clickableSources.length === 1) {
      onSelectSource(clickableSources[0])
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (sourceGuid: string) => {
    onSelectSource(sourceGuid)
    handleClose()
  }

  return (
    <>
      {hasClickableSource ? (
        <>
          <Button
            onClick={handleClick}
            sx={{
              textTransform: 'none',
            }}
          >
            {sourceLabel}
          </Button>
          {hasMultipleClickableSources && (
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              {Array.from(clickableLabelMap.entries()).map(([label, guid]) => (
                <MenuItem key={guid || label} onClick={() => handleMenuItemClick(guid)}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          )}
        </>
      ) : (
        <Button
          sx={{
            fontSize: 'inherit',
            textTransform: 'none',
            cursor: 'default',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {sourceLabel}
        </Button>
      )}
    </>
  )
}

export default LoadSourceLabel
